import logger from '../../utils/logger-rum';
/* eslint-disable no-mixed-spaces-and-tabs */
import React, {
	useCallback, useMemo, useRef, useState,
} from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { throttle } from 'lodash';
import Icon, {
	COLORS,
	ICONS,
} from '../general-components/icon';
import type {
	MediaItem, PresentedMediaItem,
} from '../../stores/types/media';
import PlayButton from '../media/controls/play-button';
import MediaVideoTimer from '../general-components/media-video-timer';
import { GreenroomState as GS } from '../../stores/types/types';
import { getVideoTimeAsHHMMSS, isVideo } from '../../utils/utils';
import { videoActionCommands } from '../../utils/video-machine';
import { findMediaPageBy } from '../../utils/media';
import TooltipWrapper from '../general-components/tooltips/tooltip-wrapper';
import { useGetMediaControls } from '../../contexts/media-controls';
import UseDeckControls from './use-deck-controls';

const {
	// LOADED,
	// FAIL,
	// TIMING,
	TIMECHANGE,
	MUTE,
	UNMUTE,
	VOLUME,
	PLAY,
	PAUSE,
	SCRUB,
	STOPSCRUBBING,
	// END,
} = videoActionCommands;

const clamp = (num: number, min: number, max: number) => Math.min(Math.max(num, min), max);

interface MediaControlsProps {
    // Define the props for the MediaControls component here
    mediaId: number;
    isEditingFeed: boolean;
    showbuilderMediaItem?: PresentedMediaItem;
    onSelectMediaFeedChange?: React.Dispatch<React.SetStateAction<number | undefined>>;
    isForShotEditorModal?: boolean;
    host?: boolean;
    isDeck?: boolean;
    timeBubbleRef: React.RefObject<HTMLDivElement | null>;
    updateShowbuilderMedia:Function;
}

export default function MediaControlsUi({
	isEditingFeed,
	showbuilderMediaItem,
	mediaId,
	isForShotEditorModal,
	isDeck,
	timeBubbleRef,
	updateShowbuilderMedia,
	onSelectMediaFeedChange = () => { },
}: MediaControlsProps) {
	// Implement the logic for the MediaControls component here
	const media = useSelector((state: GS) => state.MediaReducer.media);
	const host = useSelector((state: GS) => state.SessionReducer.host);
	const presentedMedia = useSelector((state: GS) => state.MediaReducer.presentedMedia);
	const mediaPages = useSelector(
		(state: GS) => state.MediaReducer.mediaPages,
	);

	const scrubBarRef = useRef<HTMLInputElement>(null);
	const onMouseMoveDebounce = useRef<NodeJS.Timeout>();
	const onInputDebounce = useRef<NodeJS.Timeout>();
	const [bubblePos, setBubblePos] = useState(0);
	const [mouseoverValue, setMouseoverValue] = useState(0);
	const onChangeDebounce = useRef<NodeJS.Timeout>();
	const userPaused = useRef(true);
	const volumeChangeDebounce = useRef<NodeJS.Timeout | null>(null);

	const {
		nextPage,
		previousPage,
	} = UseDeckControls({
		mediaId,
		isEditingFeed,
		showbuilderMediaItem,
		updateShowbuilderMedia,
		onSelectMediaFeedChange,
		isForShotEditorModal,
	});

	const mediaControl = useGetMediaControls(`${mediaId}${isForShotEditorModal ? '-draft' : ''}`);
	const send = mediaControl?.send;
	const state = mediaControl?.state;
	const paused = state?.paused ?? true;
	const elapsed = state?.elapsed ?? 0;
	const duration = state?.duration ?? 8;
	const volume = state?.volume ?? 1;
	const muted = state?.muted ?? false;
	const shownVolume = state?.shownVolume ?? 1;
	const bufferedDuration = state?.bufferedDuration ?? 0;

	const mediaItem = useMemo(
		() => media.find((m: MediaItem) => m.id === mediaId),
		[mediaId, media],
	);
	const numberOfPages = useMemo(() => mediaItem?.images.split(',').length, [mediaItem?.images]);
	const presentedMediaItem = useMemo(
		() => (isEditingFeed ? showbuilderMediaItem : presentedMedia
			.find((m) => m.mediaId === mediaId)),
		[presentedMedia, mediaId, showbuilderMediaItem, isEditingFeed],
	);

	function onMouseMove(e: React.MouseEvent<HTMLInputElement, MouseEvent>) {
		if (!scrubBarRef.current) return;

		e.currentTarget?.blur();

		clearTimeout(onMouseMoveDebounce?.current);
		onMouseMoveDebounce.current = setTimeout(() => {
			if (timeBubbleRef.current && scrubBarRef.current) {
				const scrubRect = scrubBarRef.current.getBoundingClientRect();
				const bubbleWidth = timeBubbleRef.current?.offsetWidth ?? 0;
				const time = (e.nativeEvent.offsetX / scrubRect.width) * duration;
				const pos = (e.clientX - scrubRect.left);

				const clampedAndFlooredTime = Math.floor(clamp(time, 0, duration));

				const halfPercWidth = scrubRect.width / 200;
				const clampedPos = Math.floor(clamp(pos, halfPercWidth, scrubRect.width - halfPercWidth));
				const truePos = clampedPos - (bubbleWidth / 2);

				if (mouseoverValue !== clampedAndFlooredTime) {
					setMouseoverValue(clampedAndFlooredTime);
					setBubblePos(truePos);
				}
			}
		}, 1);
	}
	const onScrubMouseUp = useCallback(() => {
		logger.debug('feed-media.tsx onScrubMouseUp');
		if (send) {
			send(STOPSCRUBBING, { from: 'onScrubMouseUp' });
		}
	}, [send]);

	const onInput = (e: React.FormEvent<HTMLInputElement>) => {
		e.currentTarget?.blur();
		const inputValue = e.currentTarget?.value;

		clearTimeout(onInputDebounce?.current);
		onInputDebounce.current = setTimeout(() => {
			if (!inputValue) {
				return;
			}
			if (Math.floor(mouseoverValue) !== Math.floor(elapsed)) {
				logger.debug('feed-media.tsx onInput');
				send?.(SCRUB, { currentTime: mouseoverValue, from: 'onInput' });
				window.addEventListener('mouseup', onScrubMouseUp, { once: true });
			}
		}, 1);
	};

	const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		e.currentTarget?.blur();

		clearTimeout(onChangeDebounce.current);
		onChangeDebounce.current = setTimeout(() => {
			logger.debug('feed-media.tsx TIMECHANGE scrubbar onChange', { currentTime: elapsed });
			send?.(TIMECHANGE, { currentTime: mouseoverValue, duration, from: 'scrubbar onChange' });
		}, 1);
	};

	const timeScrubCss = useMemo(() => {
		const elapsedPerc = ((elapsed / duration) * 100) + 0.1;
		const bufferedPerc = Math.min(((bufferedDuration / duration) * 100), 100);

		// Three sections: elapsed (white), buffered (medium gray), unbuffered (light gray)
		if (bufferedDuration > elapsed) {
			return {
				background: `linear-gradient(to right, #FFFFFF 0%, #FFFFFF ${elapsedPerc}%, rgba(255, 255, 255, 0.6) ${elapsedPerc}%, rgba(255, 255, 255, 0.6) ${bufferedPerc}%, rgba(255, 255, 255, 0.3) ${bufferedPerc}%, rgba(255, 255, 255, 0.3) 100%)`
			};
		}

		// Fallback to two sections if no buffered data
		return {
			background: `linear-gradient(to right, #FFFFFF 0%, #FFFFFF ${elapsedPerc}%, rgba(255, 255, 255, 0.3) ${elapsedPerc}%, rgba(255, 255, 255, 0.3) 100%)`
		};
	}, [elapsed, duration, bufferedDuration]);

	const volumeScrubCss = useMemo(() => ({ background: `linear-gradient(to right, white 0%, white ${muted ? 0 : shownVolume * 100}%, rgba(255, 255, 255, 0.3) ${muted ? 0 : shownVolume * 100}%, rgba(255, 255, 255, 0.3) 100%)` }), [shownVolume, muted]);

	const urlOfCurrentPage = useMemo(() => {
		const currentPage = presentedMediaItem?.currentPage ?? 1;
		const imageArray = mediaItem?.media_data.image_array ?? [''];
		return imageArray[currentPage - 1] ?? '';
	}, [
		mediaItem?.media_data.image_array,
		presentedMediaItem?.currentPage,
	]);

	const currentMediaPage = useMemo(
		() => findMediaPageBy(mediaPages, urlOfCurrentPage, presentedMediaItem?.mediaId),
		[mediaPages, urlOfCurrentPage, presentedMediaItem?.mediaId],
	);

	const currentPageIsVideo = useMemo(() => isVideo(urlOfCurrentPage), [urlOfCurrentPage]);

	// Know user initiated pause state immediately in this component
	const setVideoPausedState = (toPaused: boolean): void => {
		if (toPaused) {
			send?.(PAUSE, { doFireSync: true, from: 'play button' });
			userPaused.current = true;
		} else {
			send?.(PLAY, { doFireSync: true, from: 'play button' });
			userPaused.current = false;
		}
	};

	const eventOnAudioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const audioValueChange = event.target.valueAsNumber;
		if (audioValueChange === 0 || audioValueChange) {
			// We send it immediately to give the UI a smooth update
			send?.(VOLUME, {
				shownVolume: audioValueChange,
				doFireSync: true,
				from: 'volume onChange',
				currentMediaPage,
			});
		}
	};

	return (
        <>
    {!currentPageIsVideo
        && <>
            <div className='width-full align-items-center presentation-controls'>
                {presentedMediaItem && (numberOfPages ?? 0) > 1
                    && <button
                        className='background-lighter round padding-medium control'
                        data-testid="slideDeckMediaPresentationsControlLeftVisible"
                        disabled={presentedMediaItem?.currentPage === 1} onClick={(e) => {
                        	previousPage?.();
                        	e.currentTarget.blur();
                        }}>
                        <Icon name={ICONS.KEYBOARD_ARROW_LEFT} size={9} color={COLORS.GRAY} />
                    </button>
                }
                <div className="controls-pages">
                    {
                    presentedMediaItem
                    && presentedMediaItem.currentPage
                    && (numberOfPages ?? 0) > 1
                    && <span>{presentedMediaItem?.currentPage}/{(numberOfPages ?? 0)}</span>
                    }
                </div>
                {presentedMediaItem && (numberOfPages ?? 0) > 1
                    && <button
                        onClick={(e) => {
                        	nextPage?.();
                        	e.currentTarget.blur();
                        }}
                        className='background-lighter round padding-medium control'
                        disabled={presentedMediaItem?.currentPage === (numberOfPages ?? 0)}
                        data-testid="slideDeckMediaPresentationsControlRightVisible"
                    >
                        <Icon name={ICONS.KEYBOARD_ARROW_RIGHT} size={9} color={COLORS.GRAY} />
                    </button>
                }
            </div>
        </>
    }
	{currentPageIsVideo
			&& <div className="width-full align-items-center media-controls" data-testid='videoMediaControlsVisible'>
				{presentedMediaItem && (numberOfPages ?? 0) > 1
					&& <>
						<button
							onClick={(e) => {
								previousPage?.();
								e.currentTarget.blur();
							}}
							className='height-full control left'
							disabled={presentedMediaItem.currentPage === 1}
						>
							<Icon
								name={ICONS.KEYBOARD_ARROW_LEFT}
								size={9}
								color={presentedMediaItem.currentPage === 1
									? COLORS.GRAY
									: COLORS.WHITE}
							/>
						</button>
					</>
				}
				<PlayButton
					paused={paused ?? true}
					videoPlayPause={setVideoPausedState}
					className={'play-button-canvas'}
					isForShotEditorModal={isForShotEditorModal}
				/>
				<MediaVideoTimer duration={duration ?? 6} elapsed={elapsed ?? 0} />

				<div className={
					classNames(
						'align-items-center position-relative flex-grow scrub-wrap',
						{
							presenter: !host,
							deck: isDeck,
						},
					)}
				>

						<div
							ref={timeBubbleRef}
							className='time-bubble'
							style={{ left: bubblePos }}
						>
							{getVideoTimeAsHHMMSS(mouseoverValue)}
						</div>

					<input
						type="range"
						className="scrubbar"
						onChange={onChange}
						onInput={onInput}
						onMouseMove={onMouseMove}
						value={elapsed}
						style={timeScrubCss}
						max={duration || 0}
						min={0}
						step={0.001}
						ref={scrubBarRef}
					/>
				</div>
				{<div className={'align-items-center position-relative padding-left-medium vol-wrap'}>
					{!isForShotEditorModal
						? <div className={classNames('vol-icon-wrap', { muted })} onClick={() => {
							logger.debug(`feed-media.tsx ${muted ? 'UNMUTE' : 'MUTE'} volume button`, { doFireSync: true });
							send?.(muted ? UNMUTE : MUTE, {
								doFireSync: true,
								from: 'volume button',
								currentMediaPage,
							});
						}}>
							<Icon className='margin-right-small' name={muted ? ICONS.AUDIO_MUTED : ICONS.SOUND} size={18} color={muted ? COLORS.GRAY : COLORS.WHITE} />
						</div>
						: <TooltipWrapper message="Cannot unmute videos in Edit Shot view">
							<Icon
								className='margin-right-small margin-top-small'
								name={muted ? ICONS.AUDIO_MUTED : ICONS.SOUND}
								size={18}
								color={muted ? COLORS.GRAY : COLORS.WHITE}
							/>
						</TooltipWrapper>
					}
					{!isForShotEditorModal
						&& <input
							type="range"
							className="volume"
							onMouseDown={() => {
								logger.debug('feed-media.tsx VOLUME onMouseDown', { volume, doFireSync: true });
								send?.(VOLUME, {
									shownVolume,
									doFireSync: true,
									from: 'volume onMouseDown',
									currentMediaPage,
								});
							}}
							onChange={eventOnAudioChange}
							onMouseUp={(e) => {
								e.currentTarget.blur();
							}}
							value={muted ? 0 : shownVolume}
							step={0.01}
							min={0}
							max={1}
							style={volumeScrubCss}
						>
						</input>
					}
				</div>}
				{presentedMediaItem && (numberOfPages ?? 0) > 1
					&& <>
						<button
							onClick={(e) => {
								nextPage?.();
								e.currentTarget.blur();
							}}
							className='height-full control right'
							disabled={
								presentedMediaItem?.currentPage === numberOfPages
							}
						>
							<Icon
								name={ICONS.KEYBOARD_ARROW_RIGHT}
								size={9}
								color={
									presentedMediaItem?.currentPage === numberOfPages
										? COLORS.GRAY
										: COLORS.WHITE
								}
							/>
						</button>
						<div className="controls-pages">
							{
								presentedMediaItem.currentPage
								&& (numberOfPages ?? 0) > 1
								&& <span>
										{presentedMediaItem?.currentPage}/{numberOfPages}
								</span>
							}
						</div>
					</>
				}
			</div>
		}
        </>
	);
}
